import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchServicesAPI,
  fetchServiceByIdAPI,
  deleteServiceAPI,
  updateServiceAPI,
  createServiceAPI,
} from "../../services/servicesAPI";

// ✅ Fetch all services
export const fetchServices = createAsyncThunk(
  "services/fetchServices",
  async () => {
    const response = await fetchServicesAPI();
    return response.data.data;
  }
);

// ✅ Fetch single service by ID
export const fetchServiceById = createAsyncThunk(
  "services/fetchServiceById",
  async (id) => {
    const response = await fetchServiceByIdAPI(id);
    return response.data.data;
  }
);

// ✅ Delete service
export const deleteService = createAsyncThunk(
  "services/deleteService",
  async (id) => {
    await deleteServiceAPI(id);
    return id;
  }
);

// ✅ Update service
export const updateService = createAsyncThunk(
  "services/updateService",
  async ({ id, data }) => {
    const response = await updateServiceAPI(id, data);
    return response.data.data;
  }
);

// ✅ Create new service
export const createService = createAsyncThunk(
  "services/createService",
  async (data) => {
    const response = await createServiceAPI(data);
    return response.data.data;
  }
);

const initialState = {
  items: [],
  detail: null,
  status: "idle",        // for list (fetchServices)
  detailStatus: "idle",  // for fetchServiceById
  error: null,
};

const servicesSlice = createSlice({
  name: "services",
  initialState,
  reducers: {
    clearDetail: (state) => {
      state.detail = null;
      state.detailStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Fetch all
      .addCase(fetchServices.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.status = "succeeded";
        const services = action.payload.products || [];
        state.items = services.map(service => ({
          uuid: service.uuid,
          name: service.name,
          title: service.title,
          description: service.description,
          active: service.active,
          product_image_url: service.product_image_url,
          created_at: service.created_at,
          updated_at: service.updated_at,
          inactive_at: service.inactive_at,
        }));
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // ✅ Fetch single
      .addCase(fetchServiceById.pending, (state) => {
        state.detailStatus = "loading";
        state.detail = null;
      })
      .addCase(fetchServiceById.fulfilled, (state, action) => {
        state.detailStatus = "succeeded";
        state.detail = action.payload;
      })
      .addCase(fetchServiceById.rejected, (state, action) => {
        state.detailStatus = "failed";
        state.error = action.error.message;
      })

      // ✅ Delete
      .addCase(deleteService.fulfilled, (state, action) => {
        const id = action.payload;
        state.items = state.items.filter((s) => s.uuid !== id);
        if (state.detail?.uuid === id) {
          state.detail = null;
          state.detailStatus = "idle";
        }
      })

      // ✅ Update
      .addCase(updateService.fulfilled, (state, action) => {
        state.detail = action.payload; // refresh detail
        state.items = state.items.map((s) =>
          s.uuid === action.meta.arg.id ? action.payload : s
        );
      })

      // ✅ Create
      .addCase(createService.fulfilled, (state, action) => {
        state.items.push(action.payload);
      });
  },
});

export const { clearDetail } = servicesSlice.actions;
export default servicesSlice.reducer;
